import { LanguagesEnum } from '@/api/enums';
import { LanguageContext } from '@/common/context/LanguageContext';
import { AppButton } from '@/ui';
import { FlexContainer } from '@/ui/styled-ui';
import { getEmptyProductCardTranslations, ProductType } from '@/utils/helpers';
import { Skeleton } from 'primereact/skeleton';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledEmptyProductCard = styled(FlexContainer)<{
  type: ProductType;
  isBeige?: boolean;
}>`
  background-color: var(--beige-main);
  border-radius: ${({ isBeige }) =>
    isBeige ? 'var(--medium-border-radius)' : 'none'};
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  flex-grow: ${({ isBeige }) => (isBeige ? '1' : '0')};

  .image-container {
    position: relative;
    min-height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: var(--default-padding);
  }

  img {
    display: block;
    opacity: 1;
    transition: opacity 0.3s ease-in-out;

    &.loading {
      opacity: 0;
      position: absolute;
    }
  }

  h3 {
    font-size: var(--heading-small-font-size);
    line-height: var(--heading-small-line-height);
    font-weight: 700;
    margin-block: 0 var(--xsmall-padding);
  }

  p {
    max-width: 648px;
    margin-block: 0 var(--default-padding);
    font-size: var(--small-font-size);
    line-height: var(--small-line-height);
    font-weight: 400;
    text-align: center;
  }

  a {
    margin-bottom: var(--default-padding);
  }
`;

type EmptyProductCardProps = {
  type: ProductType;
  id?: string;
};

export const EmptyProductCard = ({ type, id }: EmptyProductCardProps) => {
  const { t } = useTranslation();
  const { language } = useContext(LanguageContext);
  const [isImageLoading, setIsImageLoading] = useState(true);

  const { title, description, bookingLinkEN, bookingLinkDK, img } =
    getEmptyProductCardTranslations(t, type);

  return (
    <StyledEmptyProductCard
      type={type}
      id={id}
      isBeige={type === ProductType.SECURITY_CULTURE_DASHBOARD}
    >
      <div className="image-container">
        {isImageLoading && <Skeleton width="580px" height="314px" />}
        <img
          src={img}
          alt={title}
          onLoad={() => setIsImageLoading(false)}
          className={isImageLoading ? 'loading' : ''}
        />
      </div>
      <h3>{title}</h3>
      <p>{description}</p>
      {bookingLinkEN && bookingLinkDK && (
        <a
          href={
            language === LanguagesEnum.DK && bookingLinkDK
              ? bookingLinkDK
              : bookingLinkEN
          }
          target="_blank"
          rel="noopener noreferrer"
        >
          <AppButton label={t('dashboard.bookAMeeting.long')} />
        </a>
      )}
    </StyledEmptyProductCard>
  );
};
